import React, { useState } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm as useHookForm } from 'react-hook-form'
import useSWRMutation from 'swr/mutation'
import type { Row } from '@tanstack/react-table'
import type { KeyedMutator } from 'swr'
import { delFetcher, patchFetcher, putFetcher } from 'api/swr'
import {
  ArrowUpIcon,
  DotsHorizontalIcon,
  Pencil1Icon,
  TrashIcon,
  UpdateIcon,
} from '@radix-ui/react-icons'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { ToastAction } from '@/components/ui/toast'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import Loader from '@/components/ui/Loader'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { MyFormInput } from 'components/ui/MyFormField'
import type { TempSpineZip } from './types'

interface DataTableRowActionsProps {
  row: Row<TempSpineZip>
  mutate?: KeyedMutator<TempSpineZip[]>
}

export const DataTableRowActions: React.FC<DataTableRowActionsProps> = ({
  row,
  mutate,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DataTableRowPin row={row} mutate={mutate} />
        <DataTableRowEdit row={row} mutate={mutate} />
        <DataTableRowDelete row={row} mutate={mutate} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const editSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  autoDeleteDays: z.coerce.number().int().min(0),
})

type EditFormValues = z.infer<typeof editSchema>

interface DataTableRowPinProps {
  row: Row<TempSpineZip>
  mutate?: KeyedMutator<TempSpineZip[]>
}

const DataTableRowPin: React.FC<DataTableRowPinProps> = ({ row, mutate }) => {
  const data = row.original
  const { toast } = useToast()
  const { trigger, isMutating } = useSWRMutation(
    `/ops-tools/temp-spine-zips/${data.id}/pin`,
    putFetcher,
  )

  const handlePin = () => {
    if (isMutating) return
    trigger(
      {},
      {
        onSuccess: () => {
          toast({
            title: 'Pinned',
            description: 'Temp spine zip has been pinned to the top.',
          })
          if (mutate) void mutate()
        },
        onError: (error: Error) => {
          toast({
            variant: 'destructive',
            title: 'Pin failed',
            description: error.message,
          })
        },
      },
    )
  }

  return (
    <DropdownMenuItem onSelect={handlePin}>
      {isMutating ? (
        <>
          <UpdateIcon className="mr-2 h-4 w-4" />
          <span>Pinning...</span>
        </>
      ) : (
        <>
          <ArrowUpIcon className="mr-2 h-4 w-4" />
          <span>Pin to top</span>
        </>
      )}
    </DropdownMenuItem>
  )
}

interface DataTableRowEditProps {
  row: Row<TempSpineZip>
  mutate?: KeyedMutator<TempSpineZip[]>
}

const DataTableRowEdit: React.FC<DataTableRowEditProps> = ({ row, mutate }) => {
  const data = row.original
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()
  const { trigger, isMutating } = useSWRMutation(
    `/ops-tools/temp-spine-zips/${data.id}`,
    patchFetcher,
  )

  const form = useHookForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: data.name,
      description: data.description ?? '',
      autoDeleteDays: data.autoDeleteDays,
    },
  })

  const onSubmit = (values: EditFormValues) => {
    if (isMutating) return
    trigger(
      {
        ...values,
        description: values.description ?? '',
      },
      {
        onSuccess: () => {
          toast({
            title: 'Updated',
            description: 'Temp spine zip has been updated.',
          })
          setIsOpen(false)
          if (mutate) void mutate()
        },
        onError: (error: Error) => {
          toast({
            variant: 'destructive',
            title: 'Update failed',
            description: error.message,
          })
        },
      },
    )
  }

  return (
    <>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault()
          form.reset({
            name: data.name,
            description: data.description ?? '',
            autoDeleteDays: data.autoDeleteDays,
          })
          setIsOpen(true)
        }}
      >
        <Pencil1Icon className="mr-2 h-4 w-4" />
        <span>Edit</span>
      </DropdownMenuItem>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit temp spine zip</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <MyFormInput name="name" form={form} label="Name" />
              <MyFormInput name="description" form={form} label="Description" />
              <MyFormInput
                name="autoDeleteDays"
                form={form}
                label="Auto delete (days, 0 = never)"
                type="number"
              />
              <p className="text-sm text-muted-foreground">
                Changing auto delete recalculates expiry from now and replaces
                the previous delayed delete task.
              </p>
              {isMutating ? (
                <Loader>Saving...</Loader>
              ) : (
                <Button type="submit">Save</Button>
              )}
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  )
}

interface DataTableRowDeleteProps {
  row: Row<TempSpineZip>
  mutate?: KeyedMutator<TempSpineZip[]>
}

const DataTableRowDelete: React.FC<DataTableRowDeleteProps> = ({
  row,
  mutate,
}) => {
  const data = row.original
  const { toast } = useToast()
  const { trigger, isMutating } = useSWRMutation(
    '/ops-tools/temp-spine-zips',
    delFetcher,
    {
      onSuccess: () => {
        toast({
          title: 'Deleted',
          description: 'Temp spine zip has been removed.',
        })
        if (mutate) void mutate()
      },
    },
  )

  const handleDelete = () => {
    trigger({ id: data.id })
  }

  const confirm = (e: Event) => {
    e.preventDefault()
    toast({
      variant: 'destructive',
      title: 'Delete temp spine zip',
      description: 'Are you sure you want to delete this record and zip file?',
      action: (
        <ToastAction altText="Confirm delete" onClick={handleDelete}>
          Delete
        </ToastAction>
      ),
    })
  }

  return (
    <DropdownMenuItem
      onSelect={confirm}
      className="text-destructive focus:bg-destructive focus:text-destructive-foreground"
    >
      {isMutating ? (
        <>
          <UpdateIcon className="mr-2 h-4 w-4" />
          <span>Deleting...</span>
        </>
      ) : (
        <>
          <TrashIcon className="mr-2 h-4 w-4" />
          <span>Delete</span>
        </>
      )}
    </DropdownMenuItem>
  )
}
